import { Currency } from '@repo/types';

function objToString(obj: Intl.NumberFormatOptions) {
    return Object.entries(obj)
        .filter(([, v]) => typeof v !== 'object' || v instanceof Date)
        .map(([k, v]) => k + v)
        .join('-');
}

const numberFormatters = new Map<string, Intl.NumberFormat>();

function getNumberFormatter(locale: string, options?: Intl.NumberFormatOptions): Intl.NumberFormat {
    const key = locale + objToString(options ?? {});

    if (numberFormatters.has(key)) return numberFormatters.get(key)!;

    numberFormatters.set(key, new Intl.NumberFormat(locale, options));
    return numberFormatters.get(key)!;
}

export function getLocaleNumberFormatNoDecimals(locale: string, num: number) {
    if (num % 1 === 0) return getNumberFormatter(locale).format(num);
    else return getLocaleNumberFormatTwoDecimals(locale, num);
}

export function getLocaleNumberFormatTwoDecimals(locale: string, num: number) {
    return getNumberFormatter(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        style: 'decimal',
    }).format(num);
}

export function formatCurrency(
    locale: string,
    currency: Currency,
    num: number,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
) {
    const formatter = getNumberFormatter(locale, {
        minimumFractionDigits,
        maximumFractionDigits,
        style: 'currency',
        ...currency,
    });

    if (currency.currencyDisplay === 'code' || currency.currencyDisplay === 'name') {
        // Allow a line break only between the value and the currency code/name by turning
        // the non-breaking space adjacent to the currency part into a regular space. The
        // thousands separators (also NBSP) stay intact, so e.g. "4 480 NOK" never splits as
        // "4" / "480 NOK". formatToParts handles both code-first (en-US) and code-last
        // (nb-NO) locales. See SB-960.
        return formatter
            .formatToParts(num)
            .map((part, i, parts) =>
                part.type === 'literal' &&
                (parts[i - 1]?.type === 'currency' || parts[i + 1]?.type === 'currency')
                    ? part.value.replace(/\xA0/g, ' ')
                    : part.value,
            )
            .join('');
    }

    return formatter.format(num);
}

export function formatCurrencyNoDecimalsIfPossible(
    locale: string,
    currency: Currency,
    num: number,
) {
    if (num % 1 === 0) return formatCurrency(locale, currency, num, 0, 2);
    else return formatCurrency(locale, currency, num);
}

export function getCurrencySymbolOrCode(currency: Currency, locale: string): string {
    if (currency.currencyDisplay === 'symbol' || currency.currencyDisplay === 'narrowSymbol') {
        const formatter = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency.currency,
            currencyDisplay: currency.currencyDisplay,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
        const parts = formatter.formatToParts(0);
        return parts.find((part) => part.type === 'currency')?.value ?? currency.currency;
    } else if (currency.currencyDisplay === 'code') {
        return currency.currency;
    } else {
        throw new Error('Unsupported currency display type');
    }
}
